import React, { useEffect, useRef, useState } from 'react';
import { Snackbar, Alert, Button, LinearProgress, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAppSelector, useAppDispatch } from '../../app/hooks';
import { operationDismissed } from '../../app/bulkProgressSlice';
import { useBulkProgressReconnect } from '../../app/useBulkProgressReconnect';
import identityService from '../../services/IdentityService';
import { BulkRowFailureModel } from '../../typings';
import BulkFailuresDialog from './BulkFailuresDialog';

const AUTO_DISMISS_MS = 5000;

const BulkProgressToast: React.FC = () => {
    useBulkProgressReconnect();
    const operations = useAppSelector(state => state.bulkProgress.operations);
    const dispatch = useAppDispatch();
    const operationList = Object.values(operations);
    const scheduledRef = useRef<Set<string>>(new Set());
    const fetchedRef = useRef<Set<string>>(new Set());
    // Undefined until the fetch settles; the dismiss timer waits for that.
    const [failures, setFailures] = useState<Record<string, BulkRowFailureModel[]>>({});
    const [viewing, setViewing] = useState<string | null>(null);

    // Once per completed operation: the report is written by the consumers as they finish, so it is
    // complete exactly when the count is. A failed fetch counts as "none" so the toast still dismisses.
    useEffect(() => {
        operationList
            .filter(op => op.completed && !fetchedRef.current.has(op.operationId))
            .forEach(op => {
                fetchedRef.current.add(op.operationId);
                identityService.getBulkOperationFailures(op.operationId)
                    .then(response => setFailures(prev => ({ ...prev, [op.operationId]: response.data ?? [] })))
                    .catch(() => setFailures(prev => ({ ...prev, [op.operationId]: [] })));
            });
    }, [operations]);

    // Auto-dismiss only once the failures are known and there are none. A toast with failures is
    // never scheduled: the admin has to read it.
    useEffect(() => {
        operationList
            .filter(op => op.completed && failures[op.operationId]?.length === 0 && !scheduledRef.current.has(op.operationId))
            .forEach(op => {
                scheduledRef.current.add(op.operationId);
                setTimeout(() => {
                    dispatch(operationDismissed(op.operationId));
                    scheduledRef.current.delete(op.operationId);
                }, AUTO_DISMISS_MS);
            });
    }, [operations, failures, dispatch]);

    return (
        <>
            {operationList.map((op, index) => {
                const progress = op.total > 0 ? (op.processed / op.total) * 100 : 0;
                const isComplete = op.completed;
                const failed = failures[op.operationId] ?? [];
                // A batch already running when the breakdown shipped reports no counters; it keeps the
                // plain count rather than claiming every row succeeded.
                const hasBreakdown = op.succeeded + op.skipped + op.failed > 0;
                const caption = !isComplete
                    ? `${op.processed} / ${op.total} users processed`
                    : hasBreakdown
                        ? [`${op.succeeded} succeeded`,
                           op.skipped > 0 ? `${op.skipped} skipped` : null,
                           op.failed > 0 ? `${op.failed} failed` : null].filter(Boolean).join(', ')
                        : `Completed — ${op.total} user(s) processed`;

                return (
                    <Snackbar
                        key={op.operationId}
                        open={true}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        sx={{ mb: index * 10 }}
                    >
                        <Alert
                            severity={isComplete ? (failed.length > 0 || op.skipped > 0 || op.failed > 0 ? 'warning' : 'success') : 'info'}
                            sx={{ width: 360, position: 'relative', pb: isComplete ? undefined : 2 }}
                            action={
                                <IconButton
                                    size="small"
                                    color="inherit"
                                    onClick={() => dispatch(operationDismissed(op.operationId))}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            }
                        >
                            <Typography variant="body2" fontWeight="bold">
                                {op.label}
                            </Typography>
                            <Typography variant="caption">{caption}</Typography>
                            {isComplete && failed.length > 0 && (
                                <Button size="small" color="inherit" sx={{ ml: 1 }} onClick={() => setViewing(op.operationId)}>
                                    View
                                </Button>
                            )}
                            {!isComplete && (
                                <LinearProgress
                                    variant="determinate"
                                    value={progress}
                                    sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, borderRadius: 0 }}
                                />
                            )}
                        </Alert>
                    </Snackbar>
                );
            })}

            {viewing && (
                <BulkFailuresDialog
                    open
                    label={operations[viewing]?.label ?? ''}
                    failures={failures[viewing] ?? []}
                    onClose={() => setViewing(null)}
                />
            )}
        </>
    );
};

export default BulkProgressToast;
